// =====================================================================
// MisAopVariance.jsx
// =====================================================================
// AOP vs Actual variance section for the MIS Expense Tracker per-property view.
// Renders the month picker, per-month KPIs, and the line-item variance table.
// Extracted from MisExpenses.jsx (iter-35e).
// =====================================================================

import { Target } from "lucide-react";
import { fmtINR, varianceTone } from "./_format";
import { KPI } from "./mis_kpi";

export default function MisAopVariance({ aop, aopMonth, setAopMonth }) {
  if (!aop || !aop.months?.length) return null;
  const m = aop.months.find((x) => x.key === aopMonth) || aop.months[0];
  if (!m) return null;

  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="mis-expense-aop">
      <AopHeader months={aop.months} aopMonth={aopMonth} setAopMonth={setAopMonth} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <KPI label="Plan total" value={fmtINR(m.plan_total)} accent="#6366F1" />
        <KPI
          label="Actual total"
          value={m.has_actual ? fmtINR(m.actual_total) : "Pending"}
          accent="#FFA500"
          tone={m.has_actual && m.actual_total > m.plan_total ? "rose" : m.has_actual ? "emerald" : ""}
        />
        <KPI
          label="Variance ₹"
          value={m.has_actual ? `${m.variance_total > 0 ? "+" : ""}${fmtINR(m.variance_total)}` : "—"}
          accent="#9370DB"
          tone={m.variance_total > 0 ? "rose" : "emerald"}
        />
        <KPI
          label="Variance %"
          value={m.variance_total_pct == null ? "—" : `${m.variance_total_pct > 0 ? "+" : ""}${m.variance_total_pct}%`}
          accent="#FB7185"
          tone={m.variance_total_pct > 0 ? "rose" : "emerald"}
        />
      </div>
      {!m.has_actual ? (
        <div className="text-center py-6 bg-indigo-50/40 border border-indigo-200 rounded-md text-[12px] text-indigo-800">
          <b>Awaiting actuals.</b> Once {m.month_label} MIS is uploaded, line-item variance will populate here automatically.
        </div>
      ) : (
        <AopCategoryTable categories={m.categories} />
      )}
    </section>
  );
}

function AopHeader({ months, aopMonth, setAopMonth }) {
  return (
    <header className="mb-3 flex items-center justify-between flex-wrap gap-2">
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700 flex items-center gap-1.5">
          <Target className="w-3.5 h-3.5" /> AOP variance · FY 26-27 plan
        </div>
        <h3 className="font-display font-black text-base">Plan vs Actuals</h3>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="aop-month-picker">
        {months.map((m) => {
          const active = aopMonth === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setAopMonth(m.key)}
              data-testid={`aop-month-${m.key}`}
              className={`text-[10.5px] px-2 py-1 rounded border tabular-nums ${
                active ? "bg-indigo-700 text-white border-indigo-700"
                       : m.has_actual ? "bg-white text-indigo-800 border-indigo-200 hover:bg-indigo-50"
                                      : "bg-zinc-50 text-zinc-500 border-zinc-200 hover:bg-zinc-100"
              }`}
            >
              {m.month_label}{m.has_actual ? "" : " ·"}
            </button>
          );
        })}
      </div>
    </header>
  );
}

function AopCategoryTable({ categories }) {
  return (
    <table className="w-full text-[12px]">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
          <th className="py-2">Category</th>
          <th className="py-2 text-right">Plan</th>
          <th className="py-2 text-right">Actual</th>
          <th className="py-2 text-right">Variance ₹</th>
          <th className="py-2 text-right">Variance %</th>
        </tr>
      </thead>
      <tbody>
        {categories.slice(0, 14).map((c) => (
          <tr key={c.category} className="border-b border-zinc-100" data-testid={`aop-row-${c.category.replace(/\W/g, "")}`}>
            <td className="py-2 font-medium text-zinc-800">{c.category}</td>
            <td className="py-2 text-right tabular-nums text-indigo-700">{fmtINR(c.plan)}</td>
            <td className="py-2 text-right tabular-nums text-zinc-900 font-bold">{fmtINR(c.actual)}</td>
            <td className={`py-2 text-right tabular-nums ${c.variance > 0 ? "text-rose-700" : "text-emerald-700"}`}>
              {c.variance > 0 ? "+" : ""}{fmtINR(c.variance)}
            </td>
            <td className="py-2 text-right">
              <span className={`inline-block px-1.5 py-0.5 rounded tabular-nums font-bold text-[11px] ${varianceTone(c.variance_pct)}`}>
                {c.variance_pct == null ? "—" : `${c.variance_pct > 0 ? "+" : ""}${c.variance_pct}%`}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
